import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import NovitaCredits from "@/components/home/NovitaCredits";
import { OpenInBrowserCard } from "@/components/home/OpenInBrowserCard";
import {
  CalendarDays, List, BarChart3, Printer, Upload, Users, Briefcase,
  Building2, ClipboardCheck, Settings, Plus, Clock, FileText, TrendingUp,
  UserPlus, Loader2, Wrench, Sparkles, Send, Truck, LayoutGrid, Gauge, CalendarX2, PieChart,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { SetupChecklist } from "@/components/SetupChecklist";
import { usePendingFixes, PendingFixesSections } from "@/components/PendingFixes";

// Iter 31mm — Home page: saluto, statistiche rapide e tessere di navigazione.
const isoDate = (d) => d.toISOString().slice(0, 10);
const weekRange = () => {
  const now = new Date();
  const day = (now.getDay() + 6) % 7; // 0 = lunedì
  const mon = new Date(now); mon.setDate(now.getDate() - day);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { from: isoDate(mon), to: isoDate(sun) };
};
const allRighe = (r) =>
  (r.giorni || [{ righe: r.righe || [] }]).flatMap((g) => g.righe || []);

// Iter 52: etichette ruoli per l'avviso accessi
const ROLE_LABELS = {
  admin: "amministratore",
  capo_cantiere: "capo cantiere",
  capo_officina: "capo officina",
  viewer: "visualizzatore",
  user: "utente",
};

const TILES = [
  { to: "/rapportino/new", k: "nuovo", icon: Plus, color: "from-[#0044FF] to-[#0033CC]", big: true },
  { to: "/quadro", k: "quadro", icon: Gauge, color: "bg-slate-900", iconColor: "text-white", perm: "can_view_dashboard" },
  { to: "/", k: "settimana", icon: CalendarDays, color: "bg-sky-100", iconColor: "text-sky-700" },
  { to: "/lista", k: "lista-rapp", icon: List, color: "bg-indigo-100", iconColor: "text-indigo-700" },
  { to: "/riepilogo", k: "riepilogo", icon: BarChart3, color: "bg-emerald-100", iconColor: "text-emerald-700" },
  { to: "/da-inviare", k: "da-inviare", icon: Send, color: "bg-lime-100", iconColor: "text-lime-700", noViewer: true, badge: "toSend" },
  { to: "/da-vidimare", k: "da-vidimare", icon: ClipboardCheck, color: "bg-amber-100", iconColor: "text-amber-700", admin: true, badge: "unvid" },
  { to: "/da-sistemare", k: "da-sistemare", icon: Wrench, color: "bg-orange-100", iconColor: "text-orange-700", admin: true, badge: "fixes" },
  { to: "/stampe", k: "stampe-ore", icon: Printer, color: "bg-rose-100", iconColor: "text-rose-700", noCapo: true },
  { to: "/controllo-ore", k: "controllo-ore", icon: CalendarX2, color: "bg-red-100", iconColor: "text-red-700", perm: "can_view_controllo_ore" },
  { to: "/statistiche", k: "statistiche", icon: TrendingUp, color: "bg-violet-100", iconColor: "text-violet-700", noCapo: true },
  { to: "/import", k: "importa", icon: Upload, color: "bg-cyan-100", iconColor: "text-cyan-700", noCapo: true, noViewer: true },
  { to: "/anagrafica/dipendenti", k: "dipendenti", icon: Users, color: "bg-orange-100", iconColor: "text-orange-700", noCapo: true },
  { to: "/anagrafica/commesse", k: "commesse", icon: Briefcase, color: "bg-teal-100", iconColor: "text-teal-700" },
  { to: "/anagrafica/clienti", k: "clienti", icon: Building2, color: "bg-fuchsia-100", iconColor: "text-fuchsia-700", admin: true },
  { to: "/programma", k: "programma", icon: LayoutGrid, color: "bg-stone-200", iconColor: "text-stone-700", perm: "can_view_risorse" },
  { to: "/risorse", k: "risorse", icon: Truck, color: "bg-yellow-100", iconColor: "text-yellow-700", perm: "can_view_risorse" },
  { to: "/impostazioni", k: "impostazioni", icon: Settings, color: "bg-slate-200", iconColor: "text-slate-700", admin: true },
];

import { tourDoneKey } from "@/components/OnboardingTour";

export default function HomePage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation("app");
  const isAdmin = user?.role === "admin";
  // Iter 61: finché il tutorial non è completato, sospendi i dialog automatici
  const [tourDone, setTourDone] = useState(() => {
    try { return !!localStorage.getItem(tourDoneKey(user?.email)); } catch (_) { return true; }
  });
  useEffect(() => {
    const onDone = () => setTourDone(true);
    window.addEventListener("il-tour-done", onDone);
    return () => window.removeEventListener("il-tour-done", onDone);
  }, []);
  const isCapo = user?.role === "capo_cantiere" || user?.role === "capo_officina";
  const isViewer = user?.role === "viewer";
  const [weekRaps, setWeekRaps] = useState([]);
  const [unvid, setUnvid] = useState(0);
  const [unvidOre, setUnvidOre] = useState(0);
  const [toSend, setToSend] = useState(0);
  // Iter 48: avvisi "da sistemare" condivisi (hook) + dialog all'ingresso
  const pf = usePendingFixes(isAdmin);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingAutoOpened, setPendingAutoOpened] = useState(false);
  // Iter 47: avviso bozza in sospeso all'ingresso (ogni utente)
  const navigate = useNavigate();
  const [bozzaPrompt, setBozzaPrompt] = useState(null);
  const [bozzaBusy, setBozzaBusy] = useState(false);
  // Iter 38: versione app + data ultimo aggiornamento (footer)
  const [versionInfo, setVersionInfo] = useState(null);
  // Iter 39: vidimazioni fatte dagli altri amministratori
  const [vidAltrui, setVidAltrui] = useState(null);
  const [vidAltruiBusy, setVidAltruiBusy] = useState(false);
  // Iter 52: accessi al software dall'ultima conferma (admin con avviso attivo)
  const [accessi, setAccessi] = useState(null);
  const [accessiBusy, setAccessiBusy] = useState(false);
  // Iter 53b: commesse vicine o oltre il budget ore (admin)
  const [budgetAlert, setBudgetAlert] = useState(null);
  // Iter 65: ultime commesse create (striscia hero) + frase del giorno
  const [recentCommesse, setRecentCommesse] = useState([]);
  const [dailyTip, setDailyTip] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    api.get("/company/logo").then(({ data }) => {
      setCompanyLogo(data.logo || "");
      setCompanyName(data.name || "");
    }).catch(() => {});
  }, []);
  // Iter 68: info ultima esportazione gestionale (solo admin, flag utente)
  const [lastExport, setLastExport] = useState(null);

  useEffect(() => {
    if (!isAdmin || user?.show_last_export === false) return;
    api
      .get("/rapportini/export/gestionale-last")
      .then((r) => setLastExport(r.data || null))
      .catch(() => {});
    // eslint-disable-next-line
  }, [isAdmin]);

  // Iter 71: frase del giorno nella lingua scelta (si aggiorna al cambio lingua)
  useEffect(() => {
    const tipLang = (i18n.language || "it").startsWith("en") ? "en" : "it";
    api.get("/daily-tip", { params: { lang: tipLang } })
      .then(({ data }) => { if (data?.enabled && data?.text) setDailyTip(data.text); })
      .catch(() => {});
  }, [i18n.language]);

  useEffect(() => {
    const { from, to } = weekRange();
    api.get("/rapportini", { params: { date_from: from, date_to: to } })
      .then(({ data }) => setWeekRaps(data || []))
      .catch((err) => console.warn("[home] rapportini:", err?.message));
    fetch(`/version.json?t=${Date.now()}`)
      .then((r) => r.json())
      .then(setVersionInfo)
      .catch(() => {});
    // Iter 65: ultime commesse create + frase del giorno
    api.get("/commesse-recenti")
      .then(({ data }) => setRecentCommesse(Array.isArray(data) ? data : []))
      .catch(() => {});
    // Iter 47: se l'utente ha una bozza in sospeso → chiedi subito cosa fare
    if (!isViewer && !sessionStorage.getItem("bozza_prompted")) {
      api.get("/bozze/mia")
        .then(({ data }) => {
          if (data?.form) {
            sessionStorage.setItem("bozza_prompted", "1");
            setBozzaPrompt(data);
          }
        })
        .catch(() => {});
    }
    // Iter 53b/134: commesse vicine al budget — per tutti (flag show_budget_alert)
    if (user?.show_budget_alert !== false) {
      api.get("/commesse/budget-alert")
        .then(({ data }) => {
          if ((data?.totale || 0) > 0) setBudgetAlert(data);
        })
        .catch(() => {});
    }
    if (isAdmin) {
      // Iter 39: vidimazioni degli altri amministratori
      api.get("/vidimazioni/altrui")
        .then(({ data }) => {
          if ((data?.totale || 0) > 0) setVidAltrui(data);
        })
        .catch(() => {});
      // Iter 52: avviso "chi è entrato" (solo se attivo per questo admin)
      if (user?.avviso_accessi) {
        api.get("/accessi/altrui")
          .then(({ data }) => {
            if ((data?.totale || 0) > 0) setAccessi(data);
          })
          .catch(() => {});
      }
      // Iter 53b: commesse che hanno raggiunto l'80% del budget ore
      // Iter 38 → 45: manutenzione AUTOMATICA all'ingresso (una volta per
      // sessione, senza conferma): UPPERCASE codici, nomi dipendenti,
      // famiglie EG-XX, madri -OFF orfane/coppie da allineare.
      if (!sessionStorage.getItem("maint_checked")) {
        sessionStorage.setItem("maint_checked", "1");
        api.post("/admin/maintenance/run")
          .then(({ data }) => {
            const n = data?.totale_interventi || 0;
            if (n > 0) {
              toast.success(
                `🔧 Manutenzione automatica completata: ${n} ${n === 1 ? "intervento applicato" : "interventi applicati"} (codici, nomi, famiglie, commesse madre)`,
                { duration: 7000 }
              );
            }
          })
          .catch((err) =>
            console.warn("[home] manutenzione automatica fallita:", err?.message)
          );
      }
      api.get("/rapportini/unvidimati-count")
        .then(({ data }) => {
          setUnvid(data?.count || 0);
          setUnvidOre(data?.ore_settimana || 0);
        })
        .catch((err) => console.warn("[home] unvidimati:", err?.message));
    }
    // eslint-disable-next-line
  }, [isAdmin]);

  // Iter 101: conteggio rapportini personali da inviare via email (dal 01/08)
  useEffect(() => {
    if (isViewer) return;
    api.get("/rapportini-da-inviare?count_only=true")
      .then(({ data }) => setToSend(data?.count_da_inviare || 0))
      .catch((err) => console.warn("[home] da-inviare:", err?.message));
    // eslint-disable-next-line
  }, []);

  // Iter 48: apri il dialog UNA volta al caricamento se ci sono avvisi
  useEffect(() => {
    if (isAdmin && pf.loaded && pf.total > 0 && !pendingAutoOpened) {
      setPendingOpen(true);
      setPendingAutoOpened(true);
    }
  }, [isAdmin, pf.loaded, pf.total, pendingAutoOpened]);

  // Auto-chiudi quando l'ultimo avviso è stato sistemato
  useEffect(() => {
    if (pendingOpen && pf.total === 0) setPendingOpen(false);
  }, [pf.total, pendingOpen]);

  // Iter 39: segna le vidimazioni altrui come viste
  const segnaVidViste = async () => {
    setVidAltruiBusy(true);
    try {
      await api.post("/vidimazioni/segna-viste");
      setVidAltrui(null);
    } catch {
      toast.error("Operazione non riuscita");
    } finally {
      setVidAltruiBusy(false);
    }
  };

  // Iter 52: segna gli accessi come visti
  const segnaAccessiVisti = async () => {
    setAccessiBusy(true);
    try {
      await api.post("/accessi/segna-visti");
      setAccessi(null);
    } catch {
      toast.error("Operazione non riuscita");
    } finally {
      setAccessiBusy(false);
    }
  };

  // Iter 47: azioni sul prompt bozza all'ingresso
  const riprendiBozza = () => {
    setBozzaPrompt(null);
    navigate("/rapportino/new?bozza=server");
  };

  const eliminaBozza = async () => {
    setBozzaBusy(true);
    try {
      await api.delete("/bozze/mia");
      try { localStorage.removeItem("rapportino_draft_v1"); } catch { /* ignore */ }
      toast.success("Bozza eliminata: puoi creare il rapportino da zero");
      setBozzaPrompt(null);
    } catch {
      toast.error("Eliminazione non riuscita");
    } finally {
      setBozzaBusy(false);
    }
  };

  const stats = useMemo(() => {
    const ore = weekRaps.reduce(
      (s, r) => s + allRighe(r).reduce((x, rg) => x + Number(rg.ore_tot || 0), 0),
      0
    );
    return { nRaps: weekRaps.length, ore: Math.round(ore * 100) / 100 };
  }, [weekRaps]);

  const tiles = TILES.filter((tl) => {
    if (tl.admin && !isAdmin) return false;
    if (tl.perm && !isAdmin && !user?.[tl.perm]) return false;
    if (tl.to === "/impostazioni" && user?.can_manage_settings === false) return false;
    if (tl.noCapo && isCapo) return false;
    if (tl.noViewer && isViewer) return false;
    if (tl.to === "/rapportino/new" && isViewer) return false;
    return true;
  });

  const oggi = new Date().toLocaleDateString(
    (i18n.language || "it").startsWith("en") ? "en-GB" : "it-IT",
    { weekday: "long", day: "numeric", month: "long", year: "numeric" }
  );
  const nome = (user?.name || user?.email || "").split(" ")[0];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6" data-testid="home-page">
      {/* Iter 33 → 48: alert admin — avvisi "da sistemare" all'ingresso */}
      <Dialog open={pendingOpen && !bozzaPrompt && tourDone} onOpenChange={setPendingOpen}>
        <DialogContent className="max-w-lg" data-testid="pending-employees-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-amber-600" />
              {t("home.dlg.pendingTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            <PendingFixesSections pf={pf} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPendingOpen(false)} data-testid="pending-employees-later">
              {t("home.dlg.later")}
            </Button>
            <Button
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => { setPendingOpen(false); navigate("/da-sistemare"); }}
              data-testid="pending-open-da-sistemare"
            >
              <Wrench className="h-4 w-4 mr-2" />
              {t("home.dlg.openFix")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 47: prompt bozza in sospeso all'ingresso */}
      <Dialog open={!!bozzaPrompt && tourDone} onOpenChange={(o) => !o && setBozzaPrompt(null)}>
        <DialogContent data-testid="bozza-prompt-dialog">
          <DialogHeader>
            <DialogTitle>{t("home.dlg.bozzaTitle")}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-1.5">
            <p>
              {t("home.dlg.bozzaLeft")}
              {bozzaPrompt?.summary?.numero ? (
                <> (<span className="font-mono font-bold">n. {bozzaPrompt.summary.numero}</span>)</>
              ) : null}
              {bozzaPrompt?.updated_at && (
                <>
                  {t("home.dlg.savedOn")}{" "}
                  <strong>
                    {new Date(bozzaPrompt.updated_at).toLocaleString("it-IT", {
                      day: "2-digit", month: "2-digit",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </strong>
                </>
              )}
              .
            </p>
            {bozzaPrompt?.summary && (
              <p className="text-xs text-slate-500">
                {bozzaPrompt.summary.n_giorni > 0 && (
                  <>{bozzaPrompt.summary.n_giorni} {bozzaPrompt.summary.n_giorni === 1 ? "giorno" : "giorni"} · </>
                )}
                {bozzaPrompt.summary.n_righe > 0 && <>{bozzaPrompt.summary.n_righe} righe · </>}
                {(bozzaPrompt.summary.commesse || []).length > 0 && (
                  <>commesse: <span className="font-mono">{bozzaPrompt.summary.commesse.join(", ")}</span></>
                )}
              </p>
            )}
            <p>{t("home.dlg.bozzaQ")}</p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={eliminaBozza}
              disabled={bozzaBusy}
              data-testid="bozza-prompt-elimina"
            >
              {bozzaBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("home.dlg.bozzaDelete")}
            </Button>
            <Button
              className="bg-[#0044FF] hover:bg-[#0033CC]"
              onClick={riprendiBozza}
              data-testid="bozza-prompt-riprendi"
            >
              {t("home.dlg.bozzaResume")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 52: accessi al software dall'ultima conferma */}
      {isAdmin && accessi && (
        <div
          className="rounded-2xl border-2 border-sky-300 bg-sky-50 p-4 md:p-5"
          data-testid="accessi-altrui-card"
        >
          <div className="flex items-center gap-2 font-heading font-bold text-sky-900">
            <Clock className="h-5 w-5" />
            Accessi al software dall&apos;ultima conferma ({accessi.totale})
          </div>
          <ul className="mt-3 space-y-1.5">
            {(accessi.per_utente || []).map((a) => (
              <li
                key={a.user_id}
                className="text-sm text-sky-900"
                data-testid={`accesso-utente-${a.user_id}`}
              >
                <strong>{a.user_name}</strong>{" "}
                <span className="text-xs text-sky-700">
                  ({ROLE_LABELS[a.role] || a.role})
                </span>{" "}
                — {a.n_accessi} {a.n_accessi === 1 ? "accesso" : "accessi"}
                {a.orari?.length > 0 && (
                  <span className="text-xs text-sky-700">
                    {" "}·{" "}
                    {a.orari
                      .map((t) =>
                        new Date(t).toLocaleString("it-IT", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      )
                      .join(", ")}
                    {a.n_accessi > a.orari.length && " …"}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <Button
            onClick={segnaAccessiVisti}
            disabled={accessiBusy}
            className="mt-3 h-10 bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="accessi-altrui-ok"
          >
            {accessiBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            OK, visti
          </Button>
        </div>
      )}

      {/* Iter 53b/134: commesse vicine al budget (tutti, flag show_budget_alert) + casella Dashboard commesse a destra */}
      {((user?.show_budget_alert !== false && budgetAlert) || user?.can_view_dashboard_commesse !== false) && (
      <div className="md:flex md:items-stretch md:gap-4" data-testid="budget-row">
      {user?.show_budget_alert !== false && budgetAlert && (
        <div
          className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 md:p-5 flex-1 min-w-0"
          data-testid="budget-alert-card"
        >
          <div className="flex items-center gap-2 font-heading font-bold text-amber-900">
            <TrendingUp className="h-5 w-5" />
            {t("home.budgetTitle")} ({budgetAlert.totale})
          </div>
          <ul className="mt-3 space-y-1.5">
            {(budgetAlert.items || []).slice(0, 8).map((b) => (
              <li
                key={b.id}
                className="text-sm text-amber-900"
                data-testid={`budget-alert-${b.id}`}
              >
                <Link
                  to="/anagrafica/commesse"
                  className="hover:underline"
                >
                  <span className="font-mono font-bold">{b.codice}</span>
                  {b.descrizione && <> · {b.descrizione}</>}
                  {" — "}
                  <span
                    className={`font-mono font-bold ${
                      b.pct > 100 ? "text-red-600" : ""
                    }`}
                  >
                    {b.ore_tot}/{b.budget_ore} h ({b.pct}%)
                  </span>
                  {b.pct > 100 && (
                    <span className="ml-1 text-xs font-bold text-red-600">
                      BUDGET SUPERATO
                    </span>
                  )}
                </Link>
              </li>
            ))}
            {budgetAlert.items?.length > 8 && (
              <li className="text-xs text-amber-700">
                … e altre {budgetAlert.items.length - 8} commesse
              </li>
            )}
          </ul>
        </div>
      )}
      {user?.can_view_dashboard_commesse !== false && (
        <Link
          to="/dashboard-commesse"
          className={`mt-3 md:mt-0 shrink-0 rounded-2xl border-2 border-indigo-300 bg-indigo-50 p-4 md:p-5 flex items-center gap-3 hover:bg-indigo-100 transition-colors ${budgetAlert && user?.show_budget_alert !== false ? "md:w-[260px] md:flex-col md:items-start" : "md:flex-1"}`}
          data-testid="home-dashboard-commesse-link"
        >
          <div className="h-12 w-12 rounded-xl bg-indigo-600 text-white grid place-items-center shrink-0"><PieChart className="h-6 w-6" /></div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-indigo-900 leading-tight">Dashboard commesse</div>
            <div className="text-xs text-indigo-800 mt-0.5">Avanzamento ore vs budget di tutte le commesse recenti, con periodo a scelta.</div>
          </div>
        </Link>
      )}
      </div>
      )}

      {/* Iter 39: vidimazioni fatte dagli altri amministratori */}
      {isAdmin && vidAltrui && (
        <div
          className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4 md:p-5"
          data-testid="vidimazioni-altrui-card"
        >
          <div className="flex items-center gap-2 font-heading font-bold text-emerald-900">
            <ClipboardCheck className="h-5 w-5" />
            {t("home.vidTitle")}
          </div>
          <div className="mt-3 space-y-3">
            {(vidAltrui.per_vidimatore || []).map((v) => (
              <div key={v.vidimato_da} data-testid={`vid-altrui-${v.vidimato_da}`}>
                <div className="text-sm text-emerald-900">
                  L&apos;utente <strong>{v.vidimato_da_nome}</strong> ha vidimato{" "}
                  <strong>{v.items.length}</strong>{" "}
                  {v.items.length === 1 ? "rapportino" : "rapportini"}:
                </div>
                <ul className="mt-1.5 space-y-1">
                  {v.items.slice(0, 10).map((it) => (
                    <li key={it.rapportino_id} className="text-sm">
                      <Link
                        to={`/rapportino/${it.rapportino_id}`}
                        className="text-emerald-800 hover:underline"
                      >
                        <span className="font-mono font-bold">n. {it.numero}</span>
                        {it.data && (
                          <> · {new Date(it.data).toLocaleDateString("it-IT")}</>
                        )}
                        {it.commesse?.length > 0 && (
                          <>
                            {" "}· commesse:{" "}
                            <span className="font-mono">{it.commesse.join(", ")}</span>
                          </>
                        )}
                      </Link>
                    </li>
                  ))}
                  {v.items.length > 10 && (
                    <li className="text-xs text-emerald-700">
                      … e altri {v.items.length - 10} rapportini
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
          <Button
            onClick={segnaVidViste}
            disabled={vidAltruiBusy}
            className="mt-3 h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="vidimazioni-altrui-ok"
          >
            {vidAltruiBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            OK, segna come viste
          </Button>
        </div>
      )}

      {/* Iter 77: setup guidato per i nuovi account */}
      <SetupChecklist />

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-slate-900 text-white p-6 md:p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#0044FF]/30 blur-3xl" />
        <div className="absolute right-24 bottom-0 h-40 w-40 rounded-full bg-emerald-400/20 blur-2xl" />
        <div className="relative md:flex md:gap-6 md:items-stretch">
        <div className="min-w-0 flex-1">
          {companyLogo && (
            <img
              src={companyLogo}
              alt="logo azienda"
              className="float-right ml-3 mb-2 max-h-14 max-w-[130px] object-contain bg-white/95 rounded-lg p-1.5 shadow"
              data-testid="home-company-logo"
            />
          )}
          <div className="text-sm uppercase tracking-widest text-slate-300 font-semibold">{oggi}</div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl mt-1" data-testid="home-greeting">
            {t("home.greeting", { name: nome })}
          </h1>
          {companyName && (
            <div
              className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-bold text-[#8ab0ff] bg-[#0044FF]/20 border border-[#0044FF]/40 rounded-full px-3 py-1"
              data-testid="home-company-name"
            >
              <Building2 className="h-3.5 w-3.5" /> {companyName}
            </div>
          )}

          {dailyTip && (
            <div
              className="mt-3 border-l-2 border-emerald-400/70 pl-3 max-w-2xl"
              data-testid="home-daily-tip"
            >
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> {t("home.tipLabel")}
              </div>
              <div className="text-sm md:text-base text-slate-100 italic mt-0.5">
                “{dailyTip}”
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            <div className="rounded-xl bg-white/10 backdrop-blur px-4 py-3 min-w-[150px]" data-testid="home-stat-ore">
              <div className="flex items-center gap-2 text-slate-300 text-xs font-bold uppercase tracking-wide">
                <Clock className="h-3.5 w-3.5" /> {t("home.oreSett")}
              </div>
              <div className="text-2xl font-extrabold mt-0.5">{stats.ore}h</div>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur px-4 py-3 min-w-[150px]" data-testid="home-stat-rapportini">
              <div className="flex items-center gap-2 text-slate-300 text-xs font-bold uppercase tracking-wide">
                <FileText className="h-3.5 w-3.5" /> {t("home.rappSett")}
              </div>
              <div className="text-2xl font-extrabold mt-0.5">{stats.nRaps}</div>
            </div>
            {isAdmin && (
              <Link to="/da-vidimare" className="rounded-xl bg-amber-400/20 backdrop-blur px-4 py-3 min-w-[150px] hover:bg-amber-400/30 transition-colors" data-testid="home-stat-unvid">
                <div className="flex items-center gap-2 text-amber-200 text-xs font-bold uppercase tracking-wide">
                  <ClipboardCheck className="h-3.5 w-3.5" /> {t("home.daVidimare")}
                </div>
                <div className="text-2xl font-extrabold mt-0.5">{unvid}</div>
                {unvidOre > 0 && (
                  <div className="text-[11px] font-bold text-amber-200/90 mt-0.5" data-testid="home-stat-unvid-ore">
                    {t("home.daVidimareOre", { ore: unvidOre.toLocaleString("it-IT") })}
                  </div>
                )}
              </Link>
            )}
          </div>
          {recentCommesse.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/10" data-testid="home-recent-commesse">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">
                {t("home.ultimeCommesse")}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {recentCommesse.map((c) => (
                  <Link
                    key={c.id}
                    to="/anagrafica/commesse"
                    className="shrink-0 rounded-full bg-white/10 hover:bg-white/20 transition-colors px-3 py-1.5 text-xs text-white whitespace-nowrap"
                    title={c.descrizione || ""}
                  >
                    <span className="font-mono font-bold">{c.codice}</span>
                    {c.created_by_name && (
                      <span className="text-slate-300"> · {c.created_by_name}</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-5 md:mt-0 md:flex"><NovitaCredits /></div>
        </div>
      </div>

      {/* Iter 145: link del sito per entrare dal browser con le stesse credenziali */}
      <OpenInBrowserCard />

      {/* Iter 68: ultima esportazione al gestionale (admin, flag utente) */}
      {isAdmin && user?.show_last_export !== false && (
        <div
          className="rounded-2xl border border-sky-200 bg-sky-50 p-4 flex items-center gap-3"
          data-testid="home-last-export-card"
        >
          <div className="h-10 w-10 shrink-0 rounded-md bg-sky-200 text-sky-800 grid place-items-center">
            <Upload className="h-5 w-5" />
          </div>
          <div className="text-sm text-sky-900">
            <span className="font-heading font-bold">{t("home.lastExportTitle")}</span>
            {lastExport?.last_export_date ? (
              <>
                : {t("home.lastExportCovers")}{" "}
                <b data-testid="home-last-export-date">
                  {`${lastExport.last_export_date.slice(8, 10)}/${lastExport.last_export_date.slice(5, 7)}/${lastExport.last_export_date.slice(0, 4)}`}
                </b>
                {lastExport.last_export_at && (
                  <span className="text-sky-700">
                    {" "}· {t("home.lastExportDone")}{" "}
                    {`${lastExport.last_export_at.slice(8, 10)}/${lastExport.last_export_at.slice(5, 7)}/${lastExport.last_export_at.slice(0, 4)}`}
                  </span>
                )}
              </>
            ) : (
              <>: {t("home.lastExportNone")}</>
            )}
          </div>
        </div>
      )}

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {tiles.map((t2) => {
          const Icon = t2.icon;
          if (t2.big) {
            return (
              <Link
                key={t2.to}
                to={t2.to}
                data-testid="home-tile-nuovo-rapportino"
                className={`col-span-2 md:col-span-1 group rounded-2xl bg-gradient-to-br ${t2.color} text-white p-5 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/35 hover:-translate-y-0.5 transition-all`}
              >
                <div className="h-11 w-11 rounded-xl bg-white/20 grid place-items-center group-hover:scale-110 transition-transform">
                  <Icon className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div className="font-heading font-extrabold text-lg mt-3">{t(`home.tiles.${t2.k}.l`)}</div>
                <div className="text-sm text-blue-100">{t(`home.tiles.${t2.k}.d`)}</div>
              </Link>
            );
          }
          return (
            <Link
              key={t2.to}
              to={t2.to}
              data-testid={`home-tile-${t2.k}`}
              className="group relative rounded-2xl bg-white border border-slate-200 p-5 hover:border-[#0044FF]/40 hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className={`h-11 w-11 rounded-xl ${t2.color} grid place-items-center group-hover:scale-110 transition-transform`}>
                <Icon className={`h-6 w-6 ${t2.iconColor}`} strokeWidth={2.2} />
              </div>
              <div className="font-heading font-bold text-base mt-3 text-slate-900">{t(`home.tiles.${t2.k}.l`)}</div>
              <div className="text-xs text-slate-500 mt-0.5">{t(`home.tiles.${t2.k}.d`)}</div>
              {t2.badge === "unvid" && unvid > 0 && (
                <span className="absolute top-3 right-3 min-w-[22px] h-[22px] px-1.5 rounded-full bg-amber-500 text-white text-xs font-black grid place-items-center">
                  {unvid}
                </span>
              )}
              {t2.badge === "toSend" && toSend > 0 && (
                <span className="absolute top-3 right-3 min-w-[22px] h-[22px] px-1.5 rounded-full bg-lime-600 text-white text-xs font-black grid place-items-center" data-testid="home-tile-tosend-badge">
                  {toSend}
                </span>
              )}
              {t2.badge === "fixes" && pf.total > 0 && (
                <span className="absolute top-3 right-3 min-w-[22px] h-[22px] px-1.5 rounded-full bg-orange-500 text-white text-xs font-black grid place-items-center" data-testid="home-tile-fixes-badge">
                  {pf.total}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      {/* Iter 38: versione app + ultimo aggiornamento */}
      <div
        className="text-center text-xs text-slate-400 pt-4 pb-2"
        data-testid="app-version-footer"
      >
        {versionInfo?.version && <>IL-Report v{versionInfo.version}</>}
        {versionInfo?.updated_at && (
          <>
            {t("home.updatedOn")}
            {new Date(versionInfo.updated_at).toLocaleString(
              (i18n.language || "it").startsWith("en") ? "en-GB" : "it-IT",
              {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "Europe/Rome",
            })}
          </>
        )}
      </div>
    </div>
  );
}
